import { push } from 'redux-first-history';
import { all, takeEvery, put, call, select } from 'redux-saga/effects';
import actions from './actions';
import Api, { disable, enable } from '../../../../helpers/api';
import { sendRequest } from '../../../../helpers/saga';
import { logger } from '../../../../helpers/logger';
import notifications from '../../../../redux/notifications/actions';
import { keyByProperty } from '../../../../helpers/core';
import { t } from '../../../../i18n';

const api = new Api('administrators/:id');
const apiEmailResending = new Api('administrators/:id/resend_invitation_email');

function* loadData({ payload }) {
  try {
    const { id, isCreation, isEdit } = payload;

    const { formData, userData } = yield all({
      userData: !isCreation ? call(sendRequest, api.load, { id }, notifications.REQUEST_ERROR) : undefined,
      formData: isEdit || isCreation ? call(sendRequest, api.loadFormData, {}, notifications.REQUEST_ERROR) : undefined,
    });

    if (isEdit || isCreation) {
      // load form data for creation and edit
      yield put(actions.loadFormData({ data: formData.data }));
    }

    if (isCreation) {
      // create user with default data
      yield put(actions.create());
    }

    if (isEdit) {
      // load user data for edit
      yield put(actions.edit({ id, user: { ...userData.data } }));
    }

    if (userData?.data) {
      // load user data for view
      yield put(actions.loadDataSuccess({ data: userData.data }));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateUser(data) {
  try {
    const { payload } = data;
    const { editForm, id } = payload;

    const propsArray = Object.values(editForm);
    const administrator = keyByProperty(propsArray, 'name', prop => prop.value);

    administrator.country_id = administrator.country;
    administrator.state_id = administrator.state;
    delete administrator.country;
    delete administrator.state;

    const result = yield call(
      sendRequest,
      api.updateItem,
      {
        data: {
          admin: {
            ...administrator,
            role: administrator.role,
          },
        },
        id,
      },
      notifications.REQUEST_ERROR,
    );

    if (result && result.data) {
      yield put(actions.saveSuccess({ data: administrator }));
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.edited_successfully', { item: t('dash.admin.users.user') }),
        }),
      );
      yield put(push(`/users/${id}`));
    } else {
      yield put(actions.saveFail({ data: result }));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* disableUser(data) {
  try {
    const { payload } = data;
    payload.disabled = true;
    const result = yield call(
      sendRequest,
      disable,
      {
        id: payload.id,
        path: 'administrators/:id',
      },
      notifications.REQUEST_ERROR,
    );

    if (result && result.data) {
      yield put(actions.saveSuccess({ data: payload }));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* enableUser(data) {
  try {
    const { payload } = data;
    payload.disabled = false;
    const result = yield call(
      sendRequest,
      enable,
      {
        id: payload.id,
        path: 'administrators/:id',
      },
      notifications.REQUEST_ERROR,
    );

    if (result && result.data) {
      yield put(actions.saveSuccess({ data: payload }));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* deleteUser(data) {
  try {
    const { payload } = data;
    const { id } = payload;

    const result = yield call(sendRequest, api.deleteItem, { id }, notifications.REQUEST_ERROR);

    if (result && result.data) {
      yield put(actions.deleteSuccess());
    }
  } catch (error) {
    logger.error(error);
  }
}

function* addUser(data) {
  try {
    const { payload } = data;
    const { editForm } = payload;

    const propsArray = Object.values(editForm);
    const administrator = keyByProperty(propsArray, 'name', prop => prop.value);

    administrator.country_id = administrator.country;
    administrator.state_id = administrator.state;

    const result = yield call(
      sendRequest,
      api.createItem,
      {
        data: {
          admin: {
            ...administrator,
            role: administrator.role,
          },
        },
      },
      notifications.REQUEST_ERROR,
    );

    if (result && result.data) {
      yield put(
        actions.saveSuccess({
          data: {
            ...administrator,
            id: result.data.id,
          },
        }),
      );
      const { id } = result.data;
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.created_successfully', { item: t('dash.admin.users.user') }),
        }),
      );
      yield put(push(`/users/${id}`));
    } else {
      yield put(actions.saveFail({ data: result }));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* resendEmail({ payload }) {
  // drop the dispatch manually if one is already in flight.
  const isResendingEmail = yield select(state => state.User.isResendingEmail);

  if (isResendingEmail) {
    return;
  }

  yield put(actions.resendEmailStart());

  try {
    const result = yield call(
      sendRequest,
      apiEmailResending.createItem,
      {
        id: payload.id,
        path: 'administrators/:id',
      },
      notifications.REQUEST_ERROR,
    );

    if (result && result.data) {
      // uncomment in case of hiding resend email button
      // yield put(actions.loadDataSuccess({ data: { ...payload, signed_in_at_least_once: true } }));
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.sent_successfully', { item: 'Email' }),
        }),
      );
      yield put(actions.resendEmailSuccess());
    } else {
      yield put(actions.resendEmailError());
    }
  } catch (error) {
    logger.error(error);
    yield put(actions.resendEmailError());
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.ADD, addUser),
    takeEvery(actions.DELETE, deleteUser),
    takeEvery(actions.DISABLE, disableUser),
    takeEvery(actions.ENABLE, enableUser),
    takeEvery(actions.LOAD, loadData),
    takeEvery(actions.RESEND_EMAIL_REQUEST, resendEmail),
    takeEvery(actions.SAVE, updateUser),
  ]);
}
